import { useQuery } from '@tanstack/react-query'
import { useSupabase } from '@/lib/supabase-context'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { usePermissions } from '@/hooks/usePermissions'
import { novedadesService } from '@/services/novedadesService'

/** Hay con qué consultar: cliente listo, organización resuelta e identidad del usuario. */
const listoParaConsultar = (
  supabase: unknown,
  supabaseLoading: boolean,
  organizacionId: string | null,
  email: string | null,
  usuarioId: string | null,
) => !!supabase && !supabaseLoading && !!organizacionId && !!email && !!usuarioId

/**
 * ORR-217 v1 — 3 de los 4 ítems de "Novedades" (vencimientos de hoy se resuelve en el
 * componente, reusando el scoping por responsable de ProximosVencimientosCard).
 *
 * Para admin el alcance es el estudio entero: el dueño no se asigna a sí mismo como
 * responsable de clientes, y filtrar por "lo mío" le dejaba 2 secciones siempre vacías.
 *
 * Los flags de permiso NO son cosméticos: RLS no distingue roles, así que una sección oculta
 * en el JSX igual se descargaba al browser. Cada query se apaga con el permiso de su sección.
 */
export function useNovedades({ verTareas = true, verClientes = true } = {}) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const { organizacionId, usuario } = useOrganizacion()
  const { esAdmin } = usePermissions()
  // El email sale de usuarios_brainhub, no de Auth0: la base lo guarda en lowercase y Auth0
  // puede devolverlo con mayúsculas, contra filtros .eq/.neq que sí distinguen mayúsculas.
  const email = usuario?.email ?? null
  const usuarioId = usuario?.id ?? null
  // null = sin filtro por responsable (alcance estudio)
  const scopeUsuarioId = esAdmin ? null : usuarioId

  const habilitado = listoParaConsultar(supabase, supabaseLoading, organizacionId, email, usuarioId)

  const tareasAsignadas = useQuery({
    queryKey: ['novedades', 'tareas-asignadas', organizacionId, email],
    queryFn: () => novedadesService.tareasAsignadasAMi(supabase!, organizacionId!, email!),
    enabled: habilitado && verTareas,
  })

  const tareasDeOtros = useQuery({
    queryKey: ['novedades', 'tareas-de-otros', organizacionId, scopeUsuarioId, email],
    queryFn: () =>
      novedadesService.tareasAsignadasAOtros(supabase!, organizacionId!, scopeUsuarioId, email!),
    enabled: habilitado && verTareas,
  })

  const clientesNuevos = useQuery({
    queryKey: ['novedades', 'clientes-nuevos', organizacionId, scopeUsuarioId],
    queryFn: () =>
      novedadesService.clientesNuevosAsignados(supabase!, organizacionId!, scopeUsuarioId),
    enabled: habilitado && verClientes,
  })

  return {
    esAdmin,
    tareasAsignadas: tareasAsignadas.data ?? [],
    tareasDeOtros: tareasDeOtros.data ?? [],
    clientesNuevos: clientesNuevos.data ?? [],
    isLoading: tareasAsignadas.isLoading || tareasDeOtros.isLoading || clientesNuevos.isLoading,
  }
}
